import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  User, Home, Send, Search, Plus, CheckCircle2, Loader2,
  Building2, Phone, Mail, PoundSterling, MapPin, ArrowRight
} from "lucide-react";
import { API } from "@/App";

// Helper to show detailed error messages
const showError = (error, defaultMsg = "An error occurred") => {
  console.error("API Error:", error);
  const detail = error.response?.data?.detail;
  
  if (Array.isArray(detail)) {
    // Validation errors come as array
    const messages = detail.map(e => {
      const field = e.loc?.slice(-1)[0] || "field";
      return `${field}: ${e.msg}`;
    });
    toast.error(messages.join(". ") || defaultMsg);
  } else if (typeof detail === "object") {
    toast.error(detail.msg || detail.message || defaultMsg);
  } else if (detail) {
    toast.error(detail);
  } else {
    toast.error(error.message || defaultMsg);
  }
};

export const SimpleFlowsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  // Seller Flow State
  const [sellerForm, setSellerForm] = useState({
    title: "Mr",
    first_name: "",
    last_name: "",
    email: "",
    phone: ""
  });
  const [createdSeller, setCreatedSeller] = useState(null);
  const [sellerLoading, setSellerLoading] = useState(false);
  
  // Property Flow State
  const [propertyForm, setPropertyForm] = useState({
    address_line1: "",
    town: "",
    postcode: "",
    asking_price: "",
    bedrooms: "3",
    property_type: "semi_detached"
  });
  const [createdProperty, setCreatedProperty] = useState(null);
  const [propertyLoading, setPropertyLoading] = useState(false);
  
  // Instruction state
  const [instructLoading, setInstructLoading] = useState(false);
  const [instructResult, setInstructResult] = useState(null);
  
  // Existing sellers/properties for quick select
  const [existingSellers, setExistingSellers] = useState([]);
  const [existingProperties, setExistingProperties] = useState([]);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  
  // Applicant Flow State
  const [applicantForm, setApplicantForm] = useState({
    title: "Mr",
    first_name: "",
    last_name: "",
    email: "",
    phone: ""
  });
  const [applicantLoading, setApplicantLoading] = useState(false);
  const [createdApplicant, setCreatedApplicant] = useState(null);
  
  // Applicant search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  
  // Portal invite
  const [inviteLoading, setInviteLoading] = useState(false);

  // Success dialog
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successData, setSuccessData] = useState(null);

  // Load existing data on mount
  useEffect(() => {
    loadExistingData();
  }, []);

  const loadExistingData = async () => {
    try {
      const [sellersRes, propertiesRes] = await Promise.all([
        axios.get(`${API}/simple/sellers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/simple/properties`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setExistingSellers(sellersRes.data.sellers || []);
      setExistingProperties(propertiesRes.data.properties || []);
    } catch (error) {
      console.log("Could not load existing data");
    }
  };

  // ============== SELLER FLOW ==============
  
  const createSeller = async () => {
    if (!sellerForm.first_name || !sellerForm.last_name || !sellerForm.email || !sellerForm.phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSellerLoading(true);
    try {
      const res = await axios.post(`${API}/simple/seller`, sellerForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreatedSeller(res.data);
      setSelectedSeller(res.data);
      toast.success(`Seller created: ${res.data.display_name}`);
      loadExistingData();
    } catch (error) {
      showError(error, "Failed to create seller");
    } finally {
      setSellerLoading(false);
    }
  };
  
  const createProperty = async () => {
    if (!propertyForm.address_line1 || !propertyForm.postcode || !propertyForm.asking_price) {
      toast.error("Please fill in address, postcode, and asking price");
      return;
    }
    
    setPropertyLoading(true);
    try {
      const payload = {
        ...propertyForm,
        asking_price: parseFloat(propertyForm.asking_price),
        bedrooms: parseInt(propertyForm.bedrooms)
      };
      const res = await axios.post(`${API}/simple/property`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreatedProperty(res.data);
      setSelectedProperty(res.data);
      toast.success(`Property created: ${res.data.full_address}`);
      loadExistingData();
    } catch (error) {
      showError(error, "Failed to create property");
    } finally {
      setPropertyLoading(false);
    }
  };
  
  const instructProperty = async () => {
    const seller = selectedSeller || createdSeller;
    const property = selectedProperty || createdProperty;
    
    if (!seller) {
      toast.error("Please create or select a seller first");
      return;
    }
    if (!property) {
      toast.error("Please create or select a property first");
      return;
    }
    
    setInstructLoading(true);
    try {
      const res = await axios.post(`${API}/simple/instruct`, {
        seller_id: seller.seller_id || seller.applicant_id,
        property_id: property.property_id || property.instruction_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setInstructResult(res.data);
      setSuccessData({
        type: "seller",
        title: "Property Instructed!",
        message: `Portal invite sent to ${res.data.portal_email}`,
        portalUrl: res.data.portal_url,
        isNewAccount: res.data.is_new_account
      });
      setShowSuccessDialog(true);
      toast.success("Property instructed successfully!");
    } catch (error) {
      showError(error, "Failed to instruct property");
    } finally {
      setInstructLoading(false);
    }
  };

  // ============== APPLICANT FLOW ==============
  
  const searchApplicants = async () => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const res = await axios.get(`${API}/simple/applicants/search?q=${encodeURIComponent(searchQuery)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(res.data.results || []);
    } catch (error) {
      showError(error, "Search failed");
    } finally {
      setSearchLoading(false);
    }
  };
  
  const createApplicant = async () => {
    if (!applicantForm.first_name || !applicantForm.last_name || !applicantForm.email || !applicantForm.phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setApplicantLoading(true);
    try {
      const res = await axios.post(`${API}/simple/applicant`, applicantForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreatedApplicant(res.data);
      setSelectedApplicant(res.data);
      toast.success(`Applicant created: ${res.data.display_name}`);
    } catch (error) {
      showError(error, "Failed to create applicant");
    } finally {
      setApplicantLoading(false);
    }
  };
  
  const inviteToPortal = async () => {
    const applicant = selectedApplicant || createdApplicant;
    
    if (!applicant) {
      toast.error("Please create or select an applicant first");
      return;
    }
    
    setInviteLoading(true);
    try {
      const res = await axios.post(
        `${API}/simple/applicant/${applicant.applicant_id}/invite-portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccessData({
        type: "applicant",
        title: "Portal Invite Sent!",
        message: `Invite sent to ${res.data.portal_email}`,
        portalUrl: res.data.portal_url,
        isNewAccount: res.data.is_new_account
      });
      setShowSuccessDialog(true);
      toast.success("Portal invite sent!");
    } catch (error) {
      showError(error, "Failed to send portal invite");
    } finally {
      setInviteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-2xl font-bold text-slate-900">V1 Simple Mode</h1>
          <p className="text-slate-600">Simplified seller & applicant registration flows</p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <Tabs defaultValue="seller" className="space-y-6">
          <TabsList className="grid grid-cols-2 w-full max-w-md">
            <TabsTrigger value="seller" className="gap-2" data-testid="seller-tab">
              <Home className="w-4 h-4" />
              Seller Flow
            </TabsTrigger>
            <TabsTrigger value="applicant" className="gap-2" data-testid="applicant-tab">
              <User className="w-4 h-4" />
              Applicant Flow
            </TabsTrigger>
          </TabsList>

          {/* ============== SELLER TAB ============== */}
          <TabsContent value="seller" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Step 1: Create Seller */}
              <Card className="border-2 border-blue-200">
                <CardHeader className="bg-blue-50 border-b">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">1</div>
                    <div>
                      <CardTitle className="text-lg">Create Seller</CardTitle>
                      <CardDescription>Add seller details</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <Label>Title</Label>
                      <Select value={sellerForm.title} onValueChange={(v) => setSellerForm(p => ({...p, title: v}))}>
                        <SelectTrigger data-testid="seller-title">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Mr">Mr</SelectItem>
                          <SelectItem value="Mrs">Mrs</SelectItem>
                          <SelectItem value="Ms">Ms</SelectItem>
                          <SelectItem value="Miss">Miss</SelectItem>
                          <SelectItem value="Dr">Dr</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Label>First Name *</Label>
                      <Input
                        value={sellerForm.first_name}
                        onChange={(e) => setSellerForm(p => ({...p, first_name: e.target.value}))}
                        placeholder="John"
                        data-testid="seller-first-name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Last Name *</Label>
                    <Input
                      value={sellerForm.last_name}
                      onChange={(e) => setSellerForm(p => ({...p, last_name: e.target.value}))}
                      placeholder="Smith"
                      data-testid="seller-last-name"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={sellerForm.email}
                      onChange={(e) => setSellerForm(p => ({...p, email: e.target.value}))}
                      placeholder="john@example.com"
                      data-testid="seller-email"
                    />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      type="tel"
                      value={sellerForm.phone}
                      onChange={(e) => setSellerForm(p => ({...p, phone: e.target.value}))}
                      placeholder="07700 900123"
                      data-testid="seller-phone"
                    />
                  </div>
                  <Button 
                    className="w-full" 
                    onClick={createSeller} 
                    disabled={sellerLoading}
                    data-testid="create-seller-btn"
                  >
                    {sellerLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Create Seller
                  </Button>
                  
                  {(createdSeller || selectedSeller) && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <span className="text-green-800 font-medium">
                        {(createdSeller || selectedSeller).display_name}
                      </span>
                    </div>
                  )}
                  
                  {existingSellers.length > 0 && (
                    <div className="pt-2 border-t">
                      <Label className="text-xs text-slate-500">Or select existing:</Label>
                      <Select onValueChange={(v) => {
                        const seller = existingSellers.find(s => s.seller_id === v);
                        setSelectedSeller(seller);
                      }}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Choose seller..." />
                        </SelectTrigger>
                        <SelectContent>
                          {existingSellers.map(s => (
                            <SelectItem key={s.seller_id} value={s.seller_id}>
                              {s.display_name} ({s.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Step 2: Create Property */}
              <Card className="border-2 border-emerald-200">
                <CardHeader className="bg-emerald-50 border-b">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold">2</div>
                    <div>
                      <CardTitle className="text-lg">Create Property</CardTitle>
                      <CardDescription>Add property details</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  <div>
                    <Label>Address *</Label>
                    <Input
                      value={propertyForm.address_line1}
                      onChange={(e) => setPropertyForm(p => ({...p, address_line1: e.target.value}))}
                      placeholder="123 High Street"
                      data-testid="property-address"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Town</Label>
                      <Input
                        value={propertyForm.town}
                        onChange={(e) => setPropertyForm(p => ({...p, town: e.target.value}))}
                        placeholder="Bedford"
                        data-testid="property-town"
                      />
                    </div>
                    <div>
                      <Label>Postcode *</Label>
                      <Input
                        value={propertyForm.postcode}
                        onChange={(e) => setPropertyForm(p => ({...p, postcode: e.target.value.toUpperCase()}))}
                        placeholder="MK42 7AB"
                        data-testid="property-postcode"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Asking Price (£) *</Label>
                    <Input
                      type="number"
                      value={propertyForm.asking_price}
                      onChange={(e) => setPropertyForm(p => ({...p, asking_price: e.target.value}))}
                      placeholder="350000"
                      data-testid="property-price"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Bedrooms</Label>
                      <Select value={propertyForm.bedrooms} onValueChange={(v) => setPropertyForm(p => ({...p, bedrooms: v}))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1,2,3,4,5,6].map(n => (
                            <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Type</Label>
                      <Select value={propertyForm.property_type} onValueChange={(v) => setPropertyForm(p => ({...p, property_type: v}))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="detached">Detached</SelectItem>
                          <SelectItem value="semi_detached">Semi-Detached</SelectItem>
                          <SelectItem value="terraced">Terraced</SelectItem>
                          <SelectItem value="flat">Flat</SelectItem>
                          <SelectItem value="bungalow">Bungalow</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700" 
                    onClick={createProperty} 
                    disabled={propertyLoading}
                    data-testid="create-property-btn"
                  >
                    {propertyLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Building2 className="w-4 h-4 mr-2" />}
                    Create Property
                  </Button>
                  
                  {(createdProperty || selectedProperty) && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <span className="text-green-800 font-medium text-sm">
                        {(createdProperty || selectedProperty).full_address}
                      </span>
                    </div>
                  )}
                  
                  {existingProperties.length > 0 && (
                    <div className="pt-2 border-t">
                      <Label className="text-xs text-slate-500">Or select existing:</Label>
                      <Select onValueChange={(v) => {
                        const prop = existingProperties.find(p => p.instruction_id === v);
                        setSelectedProperty(prop);
                      }}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Choose property..." />
                        </SelectTrigger>
                        <SelectContent>
                          {existingProperties.map(p => (
                            <SelectItem key={p.instruction_id} value={p.instruction_id}>
                              {p.full_address} - £{p.asking_price?.toLocaleString()}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Step 3: Instruct */}
              <Card className="border-2 border-purple-200">
                <CardHeader className="bg-purple-50 border-b">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold">3</div>
                    <div>
                      <CardTitle className="text-lg">Instruct</CardTitle>
                      <CardDescription>Send portal invite</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  <div className="space-y-3">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">Seller:</p>
                      <p className="font-medium">
                        {(selectedSeller || createdSeller)?.display_name || "Not selected"}
                      </p>
                    </div>
                    <div className="flex items-center justify-center">
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">Property:</p>
                      <p className="font-medium text-sm">
                        {(selectedProperty || createdProperty)?.full_address || "Not selected"}
                      </p>
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full bg-purple-600 hover:bg-purple-700" 
                    onClick={instructProperty} 
                    disabled={instructLoading || (!selectedSeller && !createdSeller) || (!selectedProperty && !createdProperty)}
                    data-testid="instruct-btn"
                  >
                    {instructLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Instruct & Send Portal Invite
                  </Button>
                  
                  <p className="text-xs text-slate-500 text-center">
                    This will create the instruction and send the seller their portal login details.
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ============== APPLICANT TAB ============== */}
          <TabsContent value="applicant" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Step 1: Search or Create Applicant */}
              <Card className="border-2 border-blue-200 lg:col-span-2">
                <CardHeader className="bg-blue-50 border-b">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">1</div>
                    <div>
                      <CardTitle className="text-lg">Find or Create Applicant</CardTitle>
                      <CardDescription>Search existing or add new</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Search */}
                    <div className="space-y-4">
                      <h4 className="font-medium text-slate-700">Search Existing</h4>
                      <div className="flex gap-2">
                        <Input
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Name, email, or phone..."
                          onKeyDown={(e) => e.key === "Enter" && searchApplicants()}
                          data-testid="applicant-search"
                        />
                        <Button onClick={searchApplicants} disabled={searchLoading} variant="outline">
                          {searchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        </Button>
                      </div>
                      
                      {searchResults.length > 0 && (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {searchResults.map(r => (
                            <div
                              key={r.applicant_id}
                              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                selectedApplicant?.applicant_id === r.applicant_id
                                  ? "border-blue-500 bg-blue-50"
                                  : "hover:bg-slate-50"
                              }`}
                              onClick={() => setSelectedApplicant(r)}
                              data-testid="search-result-item"
                            >
                              <p className="font-medium">{r.display_name}</p>
                              <p className="text-sm text-slate-500">{r.email} • {r.phone}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Create New */}
                    <div className="space-y-4 border-l pl-6">
                      <h4 className="font-medium text-slate-700">Or Create New</h4>
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <Label className="text-xs">Title</Label>
                          <Select value={applicantForm.title} onValueChange={(v) => setApplicantForm(p => ({...p, title: v}))}>
                            <SelectTrigger data-testid="applicant-title">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Mr">Mr</SelectItem>
                              <SelectItem value="Mrs">Mrs</SelectItem>
                              <SelectItem value="Ms">Ms</SelectItem>
                              <SelectItem value="Miss">Miss</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-3">
                          <Label className="text-xs">First Name *</Label>
                          <Input
                            value={applicantForm.first_name}
                            onChange={(e) => setApplicantForm(p => ({...p, first_name: e.target.value}))}
                            placeholder="First"
                            data-testid="applicant-first-name"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Last Name *</Label>
                        <Input
                          value={applicantForm.last_name}
                          onChange={(e) => setApplicantForm(p => ({...p, last_name: e.target.value}))}
                          placeholder="Last"
                          data-testid="applicant-last-name"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Email *</Label>
                        <Input
                          type="email"
                          value={applicantForm.email}
                          onChange={(e) => setApplicantForm(p => ({...p, email: e.target.value}))}
                          placeholder="email@example.com"
                          data-testid="applicant-email"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Phone *</Label>
                        <Input
                          type="tel"
                          value={applicantForm.phone}
                          onChange={(e) => setApplicantForm(p => ({...p, phone: e.target.value}))}
                          placeholder="07700 900123"
                          data-testid="applicant-phone"
                        />
                      </div>
                      <Button 
                        className="w-full" 
                        onClick={createApplicant} 
                        disabled={applicantLoading}
                        data-testid="create-applicant-btn"
                      >
                        {applicantLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                        Create Applicant
                      </Button>
                    </div>
                  </div>
                  
                  {(selectedApplicant || createdApplicant) && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <span className="text-green-800 font-medium">
                        Selected: {(selectedApplicant || createdApplicant).display_name}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Step 2: Invite to Portal */}
              <Card className="border-2 border-purple-200">
                <CardHeader className="bg-purple-50 border-b">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold">2</div>
                    <div>
                      <CardTitle className="text-lg">Invite to Portal</CardTitle>
                      <CardDescription>Send portal credentials</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                    <p className="text-xs text-slate-500">Selected Applicant:</p>
                    {(selectedApplicant || createdApplicant) ? (
                      <>
                        <p className="font-medium">{(selectedApplicant || createdApplicant).display_name}</p>
                        <p className="text-sm text-slate-600 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {(selectedApplicant || createdApplicant).email}
                        </p>
                        <p className="text-sm text-slate-600 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {(selectedApplicant || createdApplicant).phone}
                        </p>
                      </>
                    ) : (
                      <p className="text-slate-400">No applicant selected</p>
                    )}
                  </div>
                  
                  <Button 
                    className="w-full bg-purple-600 hover:bg-purple-700" 
                    onClick={inviteToPortal}
                    disabled={inviteLoading || (!selectedApplicant && !createdApplicant)}
                    data-testid="invite-portal-btn"
                  >
                    {inviteLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Invite to Portal
                  </Button>
                  
                  <p className="text-xs text-slate-500 text-center">
                    Sends login credentials to the applicant's email address.
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <DialogTitle className="text-xl">{successData?.title}</DialogTitle>
                <DialogDescription>{successData?.message}</DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {successData?.isNewAccount && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>New account created.</strong> Login credentials were sent via email.
                </p>
              </div>
            )}
            
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Portal URL:</p>
              <a 
                href={successData?.portalUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-sm"
              >
                {successData?.portalUrl}
              </a>
            </div>
            
            <Button 
              className="w-full" 
              onClick={() => {
                setShowSuccessDialog(false);
                // Reset forms
                setSellerForm({ title: "Mr", first_name: "", last_name: "", email: "", phone: "" });
                setPropertyForm({ address_line1: "", town: "", postcode: "", asking_price: "", bedrooms: "3", property_type: "semi_detached" });
                setApplicantForm({ title: "Mr", first_name: "", last_name: "", email: "", phone: "" });
                setCreatedSeller(null);
                setCreatedProperty(null);
                setCreatedApplicant(null);
                setSelectedSeller(null);
                setSelectedProperty(null);
                setSelectedApplicant(null);
              }}
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SimpleFlowsPage;
